// i18n.ts
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
// English is in the entry bundle, not fetched. Every first visit renders in
// English (getInitialLanguage below) and nothing can paint before the strings
// exist, so a separate English chunk was one more round trip on the
// homepage's critical path — requested only after the whole bundle had run.
// Being always loaded also makes fallbackLng real: a key another locale lacks
// now falls back to English instead of rendering as the raw key.
import en from "./locales/en/translation.json";

const supportedLangs = ["en", "ar", "de", "es", "fr", "ms", "pt-BR"]; // add yours here

// Every other locale stays a chunk of its own, fetched only when chosen.
// English is left out of the glob: it is bundled above, and a dynamic import
// of a module the entry already imports statically would be dead weight.
const localeLoaders = import.meta.glob<Record<string, unknown>>(
  ["./locales/*/translation.json", "!./locales/en/translation.json"],
  { import: "default" }
);

/**
 * A locale's strings, or undefined when they cannot be loaded. Nothing is
 * registered for a locale that failed, so i18next shows its English fallback
 * for every key. That lasts until the page is reloaded: browsers remember a
 * failed module import for the life of the page, so switching back to the
 * language later does not fetch it again.
 */
const loadLocales = async (lng: string) => {
  const load = localeLoaders[`./locales/${lng}/translation.json`];
  const translations = await load?.().catch(() => undefined);
  if (!translations) {
    console.warn(
      `[i18n] Missing or invalid locale "${lng}", falling back to "en"`
    );
  }
  return translations;
};

const ensureLocale = async (lng: string) => {
  if (i18n.hasResourceBundle(lng, "translation")) return;
  const translations = await loadLocales(lng);
  if (translations) {
    i18n.addResourceBundle(lng, "translation", translations, true, true);
  }
};

const getInitialLanguage = () => {
  if (typeof window === "undefined") return "en";
  const saved = localStorage.getItem("i18nextLng");
  // if saved is not supported → fallback
  if (!saved || !supportedLangs.includes(saved)) {
    localStorage.setItem("i18nextLng", "en");
    return "en";
  }
  return saved;
};

i18n.use(initReactI18next).init({
  lng: getInitialLanguage(),
  fallbackLng: "en",
  resources: { en: { translation: en } },
  interpolation: { escapeValue: false },
});

/** Resolves once the initial language's strings are registered — at once for
 *  English, which is already bundled. */
export const initI18n = async () => {
  await ensureLocale(i18n.language);
  return true;
};

export const changeLanguage = async (lng: string) => {
  const validLang = supportedLangs.includes(lng) ? lng : "en";
  await ensureLocale(validLang);
  await i18n.changeLanguage(validLang);
  localStorage.setItem("i18nextLng", validLang);
};

export default i18n;
